import { memo } from 'react';
import { Link, NavigationNode } from '~/lib/data-contract';
import styles from './mega-menu.module.css';
import { clsx } from 'clsx';
import ItemList from '../item-list/item-list.component';
import PromotedItem from '../promoted-item/promoted-item.component';
import { MetaList } from '../meta-list/meta-list.component';
import { SubMenu } from '../sub-menu/sub-menu.component';
import { ClickTracker } from '~/shared/components';
import { useTrackMenuClick } from '../../hooks/useTrackMenuClick';

export type Props = {
    navigation: NavigationNode[];
    metaNavigation?: Link[];
    isOpenMobile?: boolean;
    selectedSubMenu?: NavigationNode | null;
    setSelectedSubMenu: (item: NavigationNode | null) => void;
    onNavigationClick?: () => void;
    className?: string;
};

export const MegaMenu = memo(
    ({
        navigation,
        metaNavigation,
        isOpenMobile,
        selectedSubMenu,
        setSelectedSubMenu,
        onNavigationClick,
        className,
    }: Props) => {
        const promotedItems = navigation?.flatMap(({ promotedChildren }) => promotedChildren || []);

        const { track } = useTrackMenuClick();

        return (
            <>
                {selectedSubMenu && (
                    <button
                        className={clsx(styles.outsideOverlay, 'cleanButton')}
                        onClick={() => setSelectedSubMenu(null)}
                    />
                )}
                <ClickTracker
                    as="nav"
                    callback={(anchor: HTMLAnchorElement) =>
                        track(anchor, selectedSubMenu || undefined)
                    }
                    className={clsx(styles.root, className, {
                        [styles.open]: isOpenMobile,
                        [styles.subListOpen]: selectedSubMenu,
                    })}
                >
                    <div className={styles.menuWrapper}>
                        <ItemList
                            items={navigation}
                            onMenuClick={setSelectedSubMenu}
                            onNavigationClick={onNavigationClick}
                            selectedItem={selectedSubMenu}
                        />
                        <ul className={clsx(styles.promotedList, 'custom-scrollbar-thin')}>
                            {promotedItems.map((child) => (
                                <li key={child.link?.id}>
                                    <PromotedItem
                                        item={child}
                                        onNavigationClick={onNavigationClick}
                                    />
                                </li>
                            ))}
                        </ul>

                        {metaNavigation && (
                            <MetaList
                                metaMenu={metaNavigation}
                                onNavigationClick={onNavigationClick}
                                onlyMobile
                            />
                        )}
                    </div>
                    {navigation.map((subMenu) => (
                        <SubMenu
                            key={subMenu.id}
                            menuContent={subMenu}
                            isSelected={selectedSubMenu?.id === subMenu.id}
                            onMenuClick={setSelectedSubMenu}
                            onNavigationClick={onNavigationClick}
                        />
                    ))}
                </ClickTracker>
            </>
        );
    }
);
